import { useEffect, useState } from "react";

/**
 * SandboxBanner
 * -------------
 * Visual "you are NOT in production" guard rail.
 *
 * When the current environment is anything other than production it paints a
 * soft (smoky) orange glow around the whole viewport plus a small dismissible
 * pill at the top. In production it renders nothing at all.
 *
 * Environment is inferred from what already exists — no new env var:
 *   - VITE_API_URL  ("//api-app.showphaze.com" = prod, "devapi-app" = dev, ...)
 *   - window.location.hostname (app.showphaze.com = prod, localhost = local)
 * Failure mode is safe: we only treat it as production when we're sure.
 *
 * Intentionally dependency-free (no MUI) so it is immune to the app's dark
 * theme (primary=white) and always renders on every route, authed or not.
 *
 * Dismiss is in-memory only: closing hides the pill for the current view;
 * a page reload brings it back. Nothing is persisted.
 */

const PROD_HOST = "app.showphaze.com";

function detectEnvironment() {
  const env = (import.meta && import.meta.env) || {};
  const host =
    (typeof window !== "undefined" && window.location && window.location.hostname) || "";
  const api = String(env.VITE_API_URL || env.VITE_API_URL_LOCAL || "");

  // ----- production: only when we're confident -----
  // note the leading "//" so "devapi-app..." does NOT match the prod host
  if (host === PROD_HOST) return "production";
  if (/\/\/api-app\.showphaze\.com/.test(api)) return "production";

  // ----- otherwise, label the sandbox flavor -----
  if (host === "localhost" || host === "127.0.0.1" || host.endsWith(".local")) return "local";
  if (/localhost|127\.0\.0\.1/.test(api)) return "local";
  if (host.startsWith("dev-") || /\/\/dev/.test(api)) return "development";
  return "sandbox";
}

function labelFor(environment) {
  switch (environment) {
    case "local":
      return "LOCAL";
    case "development":
      return "DEV";
    default:
      return "SANDBOX";
  }
}

const KEYFRAMES_ID = "sp-sandbox-banner-keyframes";
function ensureKeyframes() {
  if (typeof document === "undefined") return;
  if (document.getElementById(KEYFRAMES_ID)) return;
  const style = document.createElement("style");
  style.id = KEYFRAMES_ID;
  style.textContent = `
    @keyframes spSandboxPulse {
      0%   { opacity: 0.72; }
      50%  { opacity: 1; }
      100% { opacity: 0.72; }
    }
    @keyframes spSandboxDrop {
      from { transform: translateX(-50%) translateY(-16px); opacity: 0; }
      to   { transform: translateX(-50%) translateY(0);     opacity: 1; }
    }
  `;
  document.head.appendChild(style);
}

export default function SandboxBanner() {
  const [environment] = useState(detectEnvironment);
  const isProduction = environment === "production";

  // In-memory only — reload restores it on purpose (nothing saved).
  const [bannerOpen, setBannerOpen] = useState(true);

  useEffect(() => {
    if (!isProduction) ensureKeyframes();
  }, [isProduction]);

  if (isProduction) return null;

  const ORANGE = "255, 138, 0"; // #ff8a00

  return (
    <>
      {/* Smoky orange glow hugging the viewport edges. Never intercepts clicks. */}
      <div
        aria-hidden="true"
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 2147483000,
          pointerEvents: "none",
          borderRadius: 6,
          boxShadow: [
            `inset 0 0 0 2px rgba(${ORANGE}, 0.55)`,
            `inset 0 0 18px 4px rgba(${ORANGE}, 0.38)`,
            `inset 0 0 55px 12px rgba(${ORANGE}, 0.20)`,
            `inset 0 0 110px 30px rgba(${ORANGE}, 0.10)`,
          ].join(", "),
          animation: "spSandboxPulse 3.6s ease-in-out infinite",
        }}
      />

      {/* Dismissible pill at the top (in-memory only). */}
      {bannerOpen && (
        <div
          role="status"
          style={{
            position: "fixed",
            top: 12,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 2147483001,
            pointerEvents: "auto",
            display: "flex",
            alignItems: "center",
            gap: 10,
            maxWidth: "92vw",
            padding: "7px 10px 7px 14px",
            borderRadius: 999,
            background: `linear-gradient(135deg, rgba(${ORANGE},0.97) 0%, rgba(255,106,0,0.97) 100%)`,
            color: "#231400",
            fontFamily:
              'system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif',
            fontSize: 13,
            fontWeight: 600,
            lineHeight: 1.2,
            letterSpacing: 0.2,
            boxShadow: `0 6px 22px rgba(${ORANGE}, 0.45), 0 2px 6px rgba(0,0,0,0.25)`,
            animation: "spSandboxDrop 0.35s ease-out",
          }}
        >
          <span aria-hidden="true" style={{ fontSize: 13 }}>
            ⚠️
          </span>
          <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            Sandbox environment · <strong>{labelFor(environment)}</strong> — not production
          </span>
          <button
            type="button"
            onClick={() => setBannerOpen(false)}
            aria-label="Dismiss sandbox notice"
            title="Dismiss"
            style={{
              flex: "0 0 auto",
              width: 22,
              height: 22,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 0,
              border: "none",
              borderRadius: "50%",
              cursor: "pointer",
              color: "#231400",
              background: "rgba(0,0,0,0.14)",
              fontSize: 15,
              lineHeight: 1,
              fontWeight: 700,
            }}
          >
            ×
          </button>
        </div>
      )}
    </>
  );
}
